import { useState } from 'react';

import { PortfolioItem, PublicPortfolio } from '../../api';

import * as styled from '../styles/portfolio';
import { PortfolioLightbox } from './portfolio-lightbox';

type Props = {
  portfolio: PublicPortfolio;
  accentColor: string;
};

export const PortfolioGallery = ({ portfolio, accentColor }: Props) => {
  const hasSections = portfolio.sections.length > 0;
  const allFromBySection: PortfolioItem[] = Object.values(
    portfolio.items_by_section ?? {},
  ).flat();
  const flatList: PortfolioItem[] =
    portfolio.items && portfolio.items.length > 0
      ? portfolio.items
      : allFromBySection;

  const allItems: PortfolioItem[] = hasSections
    ? portfolio.sections
        .flatMap((section) => portfolio.items_by_section[section.id] ?? [])
        .concat(portfolio.items_by_section['uncategorized'] ?? [])
    : flatList;

  const [activeSectionId, setActiveSectionId] = useState<string | null>(
    hasSections ? (portfolio.sections[0]?.id ?? null) : null,
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (allItems.length === 0) return null;

  const activeItems: PortfolioItem[] = hasSections
    ? activeSectionId === 'uncategorized'
      ? (portfolio.items_by_section['uncategorized'] ?? [])
      : (portfolio.items_by_section[activeSectionId ?? ''] ?? [])
    : flatList;

  const tabs = hasSections
    ? [
        ...portfolio.sections,
        ...(portfolio.items_by_section['uncategorized']?.length
          ? [{ id: 'uncategorized', name: 'Other', sort_order: 999 }]
          : []),
      ]
    : [];

  return (
    <div>
      {hasSections && tabs.length > 1 && (
        <styled.TabsRow>
          {tabs.map((tab) => (
            <styled.Tab
              key={tab.id}
              onClick={() => setActiveSectionId(tab.id)}
              $active={activeSectionId === tab.id}
              $accent={accentColor}
            >
              {tab.name}
            </styled.Tab>
          ))}
        </styled.TabsRow>
      )}

      <styled.Grid>
        {activeItems.map((item, index) => {
          if (!item.asset) return null;
          return (
            <styled.Tile key={item.id} onClick={() => setLightboxIndex(index)}>
              <styled.TileMedia>
                {item.asset.kind === 'video' ? (
                  <video src={item.asset.url} muted />
                ) : (
                  <img src={item.asset.url} alt={item.caption ?? ''} />
                )}
              </styled.TileMedia>
              {item.asset.kind === 'video' && (
                <styled.PlayBadge>▶</styled.PlayBadge>
              )}
            </styled.Tile>
          );
        })}
      </styled.Grid>

      {lightboxIndex !== null && activeItems[lightboxIndex]?.asset && (
        <PortfolioLightbox
          items={activeItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() =>
            setLightboxIndex((currentIndex) =>
              Math.max(0, (currentIndex ?? 0) - 1),
            )
          }
          onNext={() =>
            setLightboxIndex((currentIndex) =>
              Math.min(activeItems.length - 1, (currentIndex ?? 0) + 1),
            )
          }
        />
      )}
    </div>
  );
};
